import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'; //-tech-debt-react-query;
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, store } from '../support/store';
import {
  CacheKeyMoodle,
  MoodleCategory,
  MoodleCourse,
  MoodleFilesResponse,
  MoodleForum,
  MoodleForumAccessInfo,
  MoodleForumDiscussion,
  MoodleForumPost,
  MoodleSection,
  MoodleUser,
  MoodleCourseCompletionUser,
  MoodleFeedbackResponsesAnalysis,
} from '../types/moodleTypes';
import { MoodleInstanceKey } from '../support/slices/moodleSlice';
import { getCookie } from '../support/cookieHelper';
import { getApiHeaders } from '../support/queries';

export type MoodleInstance = MoodleInstanceKey;

const USE_LOCAL_MOODLE = process.env.REACT_APP_LOCAL_DEVELOPMENT || null;

export let MOODLE_API_BASE = process.env.REACT_APP_API;
if (USE_LOCAL_MOODLE) {
  MOODLE_API_BASE = 'https://dev-securitytoolbox.dev-labs.ninja:8443/api';
}

function buildQuery(params: Record<string, any> = {}) {
  const q = Object.entries(params)
    .filter(([_, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return q ? `?${q}` : '';
}

export function resolveMoodleEndpoint(
  endpoint: string,
  instance: MoodleInstance = 'external'
) {
  const normalized = endpoint.replace(/^\/+/, '');

  if (!normalized.startsWith('moodle/')) return normalized;

  if (instance === 'internal') {
    if (normalized.startsWith('moodle/cyber-academy/')) {
      return normalized;
    }
    return normalized.replace(/^moodle\//, 'moodle/cyber-academy/');
  }

  return normalized.replace(/^moodle\/cyber-academy\//, 'moodle/');
}

export async function moodleFetch<T>(
  endpoint: string,
  params: Record<string, any> = {},
  options: RequestInit = {},
  instance: MoodleInstance = 'external'
): Promise<T> {
  const resolvedEndpoint = resolveMoodleEndpoint(endpoint, instance);
  const url = `${MOODLE_API_BASE.replace(/\/$/, '')}/${resolvedEndpoint}${buildQuery(params)}`;

  const method = options.method || 'GET';

  const resp = await fetch(url, {
    ...options,
    headers: {
      ...getApiHeaders(null, method, resolvedEndpoint),
      ...(options.headers || {}),
    },
    credentials: 'include',
  });

  if (!resp.ok) {
    const error: any = new Error(`Moodle API error: ${resp.status} ${resp.statusText}`);
    error.status = resp.status;
    throw error;
  }

  const data = await resp.json();

  if (Array.isArray(data)) return data as T;
  if (Array.isArray(data.sections)) return data.sections as T;
  return data as T;
}

const DEFAULT_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnMount: false as const,
  refetchOnReconnect: false,
  retry: (failureCount: number, error: any) => {
    const status = error?.status ?? error?.response?.status;
    if (status === 401 || status === 403 || status === 404) return false;
    return failureCount < 2;
  },
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000),
};

function useFetchMoodleItems<T>(
  endpoint: string,
  keyFactory: () => any[],
  params?: Record<string, any>,
  options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<T, Error> {
  return useQuery<T, Error>({
    queryKey: [...keyFactory(), instance],
    queryFn: async () => moodleFetch<T>(endpoint, params, {}, instance),
    ...{ ...DEFAULT_QUERY_OPTIONS, ...(options || {}) },
  });
}

export function filterCoursesByCategoryId(
  courses: any[] = [],
  categories: MoodleCategory[] = [],
  targetId: number
): any[] {
  if (!Array.isArray(courses) || !Array.isArray(categories) || !categories.length) return [];
  return courses.filter((course) => {
    const catId = course?.categoryid ?? course?.category?.id ?? course?.category ?? null;
    if (catId == null) return false;
    return Number(catId) === Number(targetId);
  });
}

export function useMoodleUserByEmail(
  email: string | null | undefined,
  options?: Omit<UseQueryOptions<MoodleUser | null, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleUser | null, Error> {
  return useQuery<MoodleUser | null, Error>({
    queryKey: [CacheKeyMoodle.MoodleUser, email ?? null, instance],
    queryFn: async () => {
      if (!email) return null;
      const data = await moodleFetch<any>(
        'moodle/users/search/',
        {},
        {
          method: 'POST',
          body: JSON.stringify({ criteria: { email } }),
        },
        instance
      );
      if (Array.isArray(data) && data.length > 0) return data[0] as MoodleUser;
      if (data?.users && Array.isArray(data.users) && data.users.length > 0) return data.users[0] as MoodleUser;
      return null;
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!email && (options?.enabled ?? true),
        staleTime: 10 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: (failureCount, error: any) => {
          const status = error?.status ?? error?.response?.status;
          if (status === 401 || status === 403 || status === 404) return false;
          return failureCount < 2;
        },
        ...(options || {}),
      },
  });
}

/**
 * Ensure the current learner's internal Cyber Academy account exists and they are
 * enrolled in `courseId`, so completion starts tracking the moment they open the
 * course — the eager, launch-time counterpart to the self-heal the completion read
 * performs server-side. Internal instance only; best-effort (the endpoint always
 * returns 200 with what it resolved, and we never surface an error to the learner).
 */
export function useEnsureCourseEnrollment(
  options?: UseMutationOptions<
    { moodle_user_id: number | null; provisioned: boolean } | null,
    Error,
    { email: string; courseId: number }
  >
): UseMutationResult<
  { moodle_user_id: number | null; provisioned: boolean } | null,
  Error,
  { email: string; courseId: number }
> {
  return useMutation({
    ...options,
    mutationFn: async ({ email, courseId }) => {
      if (!email || !courseId) return null;
      return moodleFetch(
        'moodle/journey-map/ensure-enrollment/',
        {},
        { method: 'POST', body: JSON.stringify({ email, course_id: courseId }) },
        'internal'
      );
    },
  });
}

export function useMoodleCourses(
  options?: Omit<UseQueryOptions<MoodleCourse[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleCourse[], Error> {
  return useFetchMoodleItems<MoodleCourse[]>(
    'moodle/courses/',
    () => [CacheKeyMoodle.MoodleCourses],
    undefined,
    options,
    instance
  );
}

export function useMoodleCourseContent(
  courseId?: number | string,
  options?: Omit<UseQueryOptions<MoodleSection[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleSection[], Error> {
  return useFetchMoodleItems<MoodleSection[]>(
    `moodle/course/${courseId}/contents/`,
    () => ['MoodleCourseContent', Number(courseId)],
    undefined,
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!courseId, ...(options || {}) },
    instance
  );
}

export function useMoodlePagesByCourses(
  courseId: number,
  options: Omit<UseQueryOptions<any[], Error>, 'queryKey' | 'queryFn'> = {},
  instance: MoodleInstance = 'external'
) {
  return useQuery<any[], Error>({
    queryKey: ['MoodlePagesByCourses', courseId, instance],
    queryFn: async () => {
      const data = await moodleFetch<any>(
        'moodle/pages/by-courses/',
        { courseids: courseId },
        {},
        instance
      );

      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.pages)) return data.pages;
      if (Array.isArray(data?.results)) return data.results;
      return [];
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!courseId && courseId > 0 && (options.enabled ?? true),
        retry: false,
        ...options,
      },
  });
}

export function useMoodleCategories(
  options?: Omit<UseQueryOptions<MoodleCategory[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleCategory[], Error> {
  return useQuery<MoodleCategory[], Error>({
    queryKey: ['MoodleCategories', instance],
    queryFn: async () => {
      const data = await moodleFetch<any>('moodle/categories/', {}, {}, instance);
      return Array.isArray(data) ? data : data?.categories ?? [];
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, ...(options || {}) },
  });
}

export function useMoodleCourseCompletion(
  courseId: number | string | undefined,
  optionsObj?: { groupId?: number | string; userId?: number | string },
  queryOptions?: Omit<UseQueryOptions<MoodleCourseCompletionUser[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleCourseCompletionUser[], Error> {
  const { groupId, userId } = optionsObj || {};
  const params: Record<string, any> = {};
  if (groupId !== undefined) params.groupid = groupId;
  if (userId !== undefined) params.userid = userId;

  return useQuery<MoodleCourseCompletionUser[], Error>({
    queryKey: ['moodleCourseCompletion', courseId, groupId ?? null, userId ?? null, instance],
    queryFn: async () => {
      if (!courseId || (!groupId && !userId)) return [];
      return moodleFetch<MoodleCourseCompletionUser[]>(
        `moodle/completion/course/${courseId}/activities/`,
        params,
        {},
        instance
      );
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!courseId && (!!groupId || !!userId),
        ...(queryOptions || {}),
      },
  });
}

export function useMoodleCourseEnrollments(
  courseId: number | string | undefined,
  groupId?: number | string,
  options?: Omit<UseQueryOptions<MoodleUser[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleUser[], Error> {
  function buildUrl(courseId: number | string, groupId?: number | string) {
    let url = `moodle/enrol/course/${courseId}/users/`;
    if (groupId !== undefined) {
      url += `?options[0][name]=groupid&options[0][value]=${encodeURIComponent(groupId)}`;
    }
    return url;
  }

  return useQuery<MoodleUser[], Error>({
    queryKey: [CacheKeyMoodle.MoodleCourseEnrollments, courseId, groupId, instance],
    queryFn: async () => {
      if (!courseId) return [];
      return moodleFetch<MoodleUser[]>(buildUrl(courseId, groupId), {}, {}, instance);
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, enabled: !!courseId, ...(options || {}) },
  });
}

export function useMoodleFiles(
  params: {
    contextid: number;
    component: string;
    filearea: string;
    itemid?: number;
    filepath?: string;
    filename?: string;
  },
  options?: Omit<UseQueryOptions<MoodleFilesResponse, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleFilesResponse, Error> {
  return useQuery<MoodleFilesResponse, Error>({
    queryKey: [CacheKeyMoodle.MoodleFiles, params, instance],
    queryFn: async () => {
      return moodleFetch<MoodleFilesResponse>('moodle/files/', params, {}, instance);
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: (options?.enabled as boolean | undefined) ?? true,
        ...(options || {}),
      },
  });
}

export function useFeedbackItems(
  feedbackId?: number,
  params?: { groupid?: number; courseid?: number },
  instance: MoodleInstance = 'external'
) {
  return useQuery({
    queryKey: ['feedbackItems', feedbackId, params, instance],
    queryFn: () => {
      if (!feedbackId) throw new Error('No feedbackId');
      return moodleFetch<any>(`moodle/feedback/${feedbackId}/items/`, params, {}, instance);
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, enabled: !!feedbackId },
  });
}

export function useSubmitFeedbackResponses(
  feedbackId: number,
  instance: MoodleInstance = 'external'
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      page: number;
      responses: any[];
      goprevious?: boolean;
      courseid: number;
      gofinish?: number;
    }) =>
      moodleFetch<any>(
        `moodle/feedback/${feedbackId}/process-page/`,
        {},
        { method: 'POST', body: JSON.stringify(payload) },
        instance
      ),
    onSuccess: (data) => {
      if (data?.jumpto >= 0) {
        queryClient.invalidateQueries({
          queryKey: ['feedbackPageItems', feedbackId, data.jumpto, instance],
        });
      }
      queryClient.invalidateQueries({ queryKey: ['feedbackLastCompleted', feedbackId, instance] });
    },
  });
}

export function useFeedbackLastCompleted(
  feedbackId?: number,
  instance: MoodleInstance = 'external'
) {
  return useQuery({
    queryKey: ['feedbackLastCompleted', feedbackId, instance],
    queryFn: () => {
      if (!feedbackId) throw new Error('No feedbackId');
      return moodleFetch<any>(`moodle/feedback/${feedbackId}/last-completed/`, {}, {}, instance);
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, enabled: !!feedbackId },
  });
}

export function useFeedbackView(
  feedbackId?: number,
  instance: MoodleInstance = 'external'
) {
  return useMutation({
    mutationFn: (payload: { moduleviewed: boolean }) => {
      if (!feedbackId) throw new Error('No feedbackId');
      return moodleFetch<any>(
        `moodle/feedback/${feedbackId}/view/`,
        {},
        { method: 'POST', body: JSON.stringify(payload) },
        instance
      );
    },
  });
}

export function useFeedbackLaunch(
  feedbackId?: number,
  instance: MoodleInstance = 'external'
) {
  return useMutation({
    mutationFn: (courseid: number) => {
      if (!feedbackId) throw new Error('No feedbackId');
      return moodleFetch<any>(
        `moodle/feedback/${feedbackId}/launch/`,
        {},
        { method: 'POST', body: JSON.stringify({ courseid }) },
        instance
      );
    },
  });
}

export function useMoodleFeedbackResponsesAnalysis(
  feedbackId?: number,
  groupId?: number,
  options?: Omit<UseQueryOptions<MoodleFeedbackResponsesAnalysis, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleFeedbackResponsesAnalysis, Error> {
  return useQuery<MoodleFeedbackResponsesAnalysis, Error>({
    queryKey: ['moodleFeedbackResponsesAnalysis', feedbackId, groupId, instance],
    queryFn: () => {
      if (!feedbackId) throw new Error('feedbackId is required');
      return moodleFetch<MoodleFeedbackResponsesAnalysis>(
        `moodle/feedback/${feedbackId}/responses-analysis/`,
        groupId ? { groupid: groupId } : {},
        {},
        instance
      );
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, enabled: !!feedbackId, ...(options || {}) },
  });
}

export function useForumsByCourses(
  courseIds?: number[] | number,
  options?: Omit<UseQueryOptions<MoodleForum[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleForum[], Error> {
  const ids = useMemo<number[] | undefined>(() => {
    if (Array.isArray(courseIds)) return courseIds;
    if (courseIds == null) return undefined;
    return [Number(courseIds)];
  }, [courseIds]);

  return useQuery<MoodleForum[], Error>({
    queryKey: ['MoodleForumsByCourses', ids, instance],
    queryFn: async () => {
      const params = new URLSearchParams();
      (ids || []).forEach((id) => params.append('courseids', String(id)));
      const qs = params.toString() ? `?${params.toString()}` : '';
      const data = await moodleFetch<any>(`moodle/forums/by-courses/${qs}`, {}, {}, instance);
      return Array.isArray(data) ? data : data?.forums ?? data ?? [];
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, enabled: !!ids?.length, ...(options || {}) },
  });
}

export function useForumAccessInfo(
  forumId?: number,
  options?: Omit<UseQueryOptions<MoodleForumAccessInfo, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleForumAccessInfo, Error> {
  return useQuery<MoodleForumAccessInfo, Error>({
    queryKey: ['MoodleForumAccessInfo', forumId, instance],
    queryFn: () => moodleFetch<MoodleForumAccessInfo>(`moodle/forums/${forumId}/access-info/`, {}, {}, instance),
    ...{ ...DEFAULT_QUERY_OPTIONS, enabled: !!forumId, ...(options || {}) },
  });
}

export function useForumCanAddDiscussion(
  forumId?: number,
  groupId?: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
) {
  return useQuery<any, Error>({
    queryKey: ['MoodleForumCanAddDiscussion', forumId, groupId, instance],
    queryFn: () => {
      const qs = groupId != null ? `?groupid=${groupId}` : '';
      return moodleFetch<any>(`moodle/forums/${forumId}/can-add-discussion/${qs}`, {}, {}, instance);
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, enabled: !!forumId, ...(options || {}) },
  });
}

export function useForumDiscussions(
  forumId?: number,
  params?: { sortby?: string; sortdirection?: string; page?: number; perpage?: number },
  options?: Omit<UseQueryOptions<{ discussions: MoodleForumDiscussion[] } | MoodleForumDiscussion[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<{ discussions: MoodleForumDiscussion[] } | MoodleForumDiscussion[], Error> {
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (params?.sortby) p.set('sortby', params.sortby);
    if (params?.sortdirection) p.set('sortdirection', params.sortdirection);
    if (params?.page != null) p.set('page', String(params.page));
    if (params?.perpage != null) p.set('perpage', String(params.perpage));
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [params]);

  return useQuery<{ discussions: MoodleForumDiscussion[] } | MoodleForumDiscussion[], Error>({
    queryKey: ['MoodleForumDiscussions', forumId, qs, instance],
    queryFn: () => moodleFetch<any>(`moodle/forums/${forumId}/discussions/${qs}`, {}, {}, instance),
    ...{ ...DEFAULT_QUERY_OPTIONS, staleTime: 30_000, enabled: !!forumId, ...(options || {}) },
  });
}

export function useDiscussionPosts(
  discussionId?: number,
  params?: { sortby?: string; sortdirection?: 'ASC' | 'DESC' },
  options?: Omit<UseQueryOptions<MoodleForumPost[], Error>, 'queryKey' | 'queryFn'>,
  instance: MoodleInstance = 'external'
): UseQueryResult<MoodleForumPost[], Error> {
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (params?.sortby) p.set('sortby', params.sortby);
    if (params?.sortdirection) p.set('sortdirection', params.sortdirection);
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [params]);

  return useQuery<MoodleForumPost[], Error>({
    queryKey: ['MoodleDiscussionPosts', discussionId, qs, instance],
    queryFn: async () => {
      const data = await moodleFetch<any>(`moodle/forum/discussion/${discussionId}/posts/${qs}`, {}, {}, instance);
      const raw = data?.posts ?? data;
      if (Array.isArray(raw)) return raw as MoodleForumPost[];
      if (raw && Array.isArray(raw.items)) return raw.items as MoodleForumPost[];
      if (raw && typeof raw === 'object') return Object.values(raw) as MoodleForumPost[];
      return [];
    },
    ...{ ...DEFAULT_QUERY_OPTIONS, staleTime: 20_000, enabled: !!discussionId, ...(options || {}) },
  });
}

export function useForumSubscribeMutation(forumId: number, instance: MoodleInstance = 'external') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subscribed: boolean) =>
      moodleFetch<any>(
        `moodle/forums/${forumId}/subscribe/`,
        {},
        { method: 'POST', body: JSON.stringify({ subscribed }) },
        instance
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['MoodleForumAccessInfo', forumId, instance] }),
  });
}

export function useForumViewMutation(forumId: number, instance: MoodleInstance = 'external') {
  return useMutation({
    mutationFn: () =>
      moodleFetch<any>(`moodle/forums/${forumId}/view/`, {}, { method: 'POST' }, instance),
  });
}

export function useDiscussionViewMutation(discussionId: number, instance: MoodleInstance = 'external') {
  return useMutation({
    mutationFn: () =>
      moodleFetch<any>(
        `moodle/forum/discussion/${discussionId}/view/`,
        {},
        { method: 'POST' },
        instance
      ),
  });
}

export function useAddDiscussionMutation(instance: MoodleInstance = 'external') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { forumid: number; subject: string; message: string; options?: any }) =>
      moodleFetch<any>(
        `moodle/forum/discussion/add/`,
        {},
        { method: 'POST', body: JSON.stringify(payload) },
        instance
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['MoodleForumDiscussions', vars.forumid, instance] });
    },
  });
}

export function useAddPostMutation(
  discussionId: number,
  instance: MoodleInstance = 'external'
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      postid?: number;
      subject: string;
      message: string;
      messageformat?: number;
      options?: { name: string; value: string }[];
    }) =>
      moodleFetch<any>(
        `moodle/forum/discussion/${discussionId}/add-post/`,
        {},
        { method: 'POST', body: JSON.stringify(payload) },
        instance
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['MoodleDiscussionPosts', discussionId, instance] });
    },
  });
}

export function useLockDiscussionMutation(
  discussionId: number,
  instance: MoodleInstance = 'external'
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locked: boolean) =>
      moodleFetch<any>(
        `moodle/forum/discussion/${discussionId}/lock/`,
        {},
        { method: 'POST', body: JSON.stringify({ locked }) },
        instance
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['MoodleDiscussionPosts', discussionId, instance] });
    },
  });
}

export function usePinDiscussionMutation(
  discussionId: number,
  instance: MoodleInstance = 'external'
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pinned: boolean) =>
      moodleFetch<any>(
        `moodle/forum/discussion/${discussionId}/pin/`,
        {},
        { method: 'POST', body: JSON.stringify({ pinned }) },
        instance
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['MoodleDiscussionPosts', discussionId, instance] });
    },
  });
}

export function useToggleFavouriteMutation(
  discussionId: number,
  instance: MoodleInstance = 'external'
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      moodleFetch<any>(
        `moodle/forum/discussion/${discussionId}/toggle-favourite/`,
        {},
        { method: 'POST' },
        instance
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['MoodleDiscussionPosts', discussionId, instance] });
    },
  });
}

export function useDeletePostMutation(
  discussionId: number,
  instance: MoodleInstance = 'external'
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: number) =>
      moodleFetch<any>(`moodle/forum/post/${postId}/delete/`, {}, { method: 'POST' }, instance),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['MoodleDiscussionPosts', discussionId, instance] });
    },
  });
}

export function useUpdatePostMutation(
  discussionId: number,
  instance: MoodleInstance = 'external'
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { postid: number; subject: string; message: string; options?: any }) =>
      moodleFetch<any>(
        `moodle/forum/post/${payload.postid}/update/`,
        {},
        {
          method: 'POST',
          body: JSON.stringify({
            subject: payload.subject,
            message: payload.message,
            options: payload.options,
          }),
        },
        instance
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['MoodleDiscussionPosts', discussionId, instance] });
    },
  });
}

type ActivityCompletionPayload = { cmid: number; completed: number };

type MoodlePageViewPayload = {
  pageId: number;
};

export function useMoodleViewPage(
  options?: UseMutationOptions<any, Error, MoodlePageViewPayload>,
  instance: MoodleInstance = 'external'
): UseMutationResult<any, Error, MoodlePageViewPayload> {
  return useMutation<any, Error, MoodlePageViewPayload>({
    ...(options || {}),
    mutationFn: async ({ pageId }) => {
      return moodleFetch(
        `moodle/page/${pageId}/view/`,
        {},
        {
          method: 'POST',
        },
        instance
      );
    },
  });
}

export function useMoodleSetActivityCompletion(
  options?: UseMutationOptions<any, Error, ActivityCompletionPayload>,
  instance: MoodleInstance = 'external'
): UseMutationResult<any, Error, ActivityCompletionPayload> {
  const queryClient = useQueryClient();

  const moodleUserId = useSelector(
    (state: RootState) => state.moodle.instances[instance]?.user?.id ?? null
  );

  return useMutation<any, Error, ActivityCompletionPayload>({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['MoodleCourseContent', instance] });
    },
    ...(options || {}),
    mutationFn: async ({ cmid, completed }) => {
      if (!moodleUserId) throw new Error(`No Moodle user for instance "${instance}"`);

      return moodleFetch(
        `moodle/completion/activity/${cmid}/user/${moodleUserId}/manual/`,
        {},
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ completed }),
        },
        instance
      );
    },
  });
}

export async function getUserCourseRoles(
  courseId: number,
  userId: number,
  instance: MoodleInstance = 'external'
) {
  const resp = await moodleFetch<{ roles: { roleid: number; shortname: string; contextid: number }[] }>(
    'moodle/users/course-roles/',
    {},
    {
      method: 'POST',
      body: JSON.stringify({ courseid: courseId, userid: userId }),
    },
    instance
  );
  return resp.roles;
}

export function useMoodleTagAreas(
  instance: MoodleInstance = 'external',
  options?: Omit<UseQueryOptions<{ areas: any[] }, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['MoodleTagAreas', instance],
    queryFn: () =>
      moodleFetch<{ areas: any[] }>('moodle/tags/areas/', {}, {}, instance),
    ...{ ...DEFAULT_QUERY_OPTIONS, ...(options || {}) },
  });
}

export function useMoodleTagCollections(
  instance: MoodleInstance = 'external',
  options?: Omit<UseQueryOptions<{ collections: any[] }, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['MoodleTagCollections', instance],
    queryFn: () =>
      moodleFetch<{ collections: any[] }>('moodle/tags/collections/', {}, {}, instance),
    ...{ ...DEFAULT_QUERY_OPTIONS, ...(options || {}) },
  });
}

export function useMoodleTags(
  query?: string,
  instance: MoodleInstance = 'external',
  options?: Omit<UseQueryOptions<{ tags: any[] }, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: ['MoodleTags', query ?? null, instance],
    queryFn: () =>
      moodleFetch<{ tags: any[] }>('moodle/tags/cloud/', query ? { query } : undefined, {}, instance),
    ...{ ...DEFAULT_QUERY_OPTIONS, ...(options || {}) },
  });
}